import React, {
	Fragment,
	useMemo,
	useState,
	useCallback,
	useEffect,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { StickyContainer } from '@sparefoot/react-core';
import { Button } from 'components/core/Button';
import { SearchBox } from 'components/search/SearchBox';
import Filter from 'components/svg/Filter';
import { ClickToCall } from 'components/facility/ClickToCall';
import { phonePropType } from 'config/propTypes';
import { Paragraph, H1 } from 'components/core/Typography';
import MapPin from 'components/svg/MapPin';
import buildHeadline from 'utils/search/buildHeadline';
import { getTollfreePhoneNumber } from 'store/modules/phone';
import { locateUser } from 'utils/GeoLocation';
import { INTERNAL_PAGE_TYPES } from 'config/constants';
import { toTitleCase } from '../../../utils/formatting';
import { simpleAmenityTextMap } from '../../../config/amenityMap';
import NearMeSubheader from '../NearMeSubheader';
import SearchFiltersTray from '../SearchFiltersTray';

import './NewSearchToolbar.scss';

export function NewSearchToolbar({
	location,
	onSearch,
	onOpenFilters,
	filtersCount = 0,
	className,
	phone: phoneProp,
	actions: actionsProp,
	sticky = false,
	hidden = false,
	vehicle,
	amenityType,
	headline,
	withinCallCenterHours,
	pageType,
	loading = false,
	// Filter tray props (for desktop dropdown)
	filtersShowing,
	closeFilters,
	searchParams,
	onFilter,
	searchType,
	unitFilters,
	facetCounts,
	useNewSearch,
}) {
	const dispatch = useDispatch();
	const storePhone = useSelector((state) => state.phone.headerPhone);
	const phone = phoneProp === undefined ? storePhone : phoneProp;

	// Geolocation state for NearMeSubheader
	const [geoSearchPending, setGeoSearchPending] = useState(false);
	const [geoError, setGeoError] = useState(false);
	const [searchStarted, setSearchStarted] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);

	// Derive hasCoords from URL params — persists across navigation unlike local state
	const hasCoords = !!(searchParams?.latitude && searchParams?.longitude);
	const isNearMePage = pageType === INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE;

	// When loading becomes true during a geo cycle, mark that search has started
	useEffect(() => {
		if (geoSearchPending && loading) {
			setSearchStarted(true);
		}
	}, [geoSearchPending, loading]);

	// Clear geoSearchPending when search completes (loading goes false AFTER it was true)
	useEffect(() => {
		if (geoSearchPending && searchStarted && !loading) {
			setGeoSearchPending(false);
			setSearchStarted(false);
			setIsUpdating(false);
			// Trigger StickyContainer height recalculation after subheader text changes
			requestAnimationFrame(() => {
				window.dispatchEvent(new Event('resize'));
			});
		}
	}, [geoSearchPending, searchStarted, loading]);

	const locating = geoSearchPending;

	const locateMe = useCallback(() => {
		if (geoSearchPending) return;
		setGeoSearchPending(true);
		setGeoError(false);
		setIsUpdating(hasCoords);

		locateUser({
			onSuccess: ({ latitude, longitude }) => {
				if (isNearMePage && onFilter) {
					onFilter({
						latitude,
						longitude,
						location: undefined,
						city: undefined,
						state: undefined,
					});
				} else {
					onSearch({
						locationParams: { latitude, longitude },
					});
				}
			},
			onError: () => {
				setGeoError(true);
				setGeoSearchPending(false);
				requestAnimationFrame(() => {
					window.dispatchEvent(new Event('resize'));
				});
			},
		});
	}, [geoSearchPending, onSearch, onFilter, isNearMePage, hasCoords]);

	const actions = useMemo(
		() =>
			actionsProp ?? {
				getTollfreePhoneNumber: (...args) =>
					dispatch(getTollfreePhoneNumber(...args)),
			},
		[actionsProp, dispatch],
	);

	// Used by SearchBox locationParams to pre-fill the input field
	const locationDetails =
		location?.city && location?.state
			? `${location.city}, ${location.state}`
			: '';

	// Headline precedence:
	// 1. Explicit headline prop (from contentTitle / SEO API)
	// 2. searchParams-driven dynamic headline via buildHeadline (SF-18689)
	// 3. Legacy vehicle / amenityType construction (backward compatible)
	let headingText;
	if (headline) {
		headingText = headline;
	} else if (searchParams) {
		headingText = buildHeadline(searchParams, locationDetails);
	} else if (vehicle) {
		headingText = `Cheap Self Storage for ${toTitleCase(vehicle)}s${
			locationDetails ? ` in ${locationDetails}` : ''
		}`;
	} else {
		headingText = `Cheap ${
			simpleAmenityTextMap[amenityType] || ''
		} Self Storage${locationDetails ? ` in ${locationDetails}` : ''}`;
	}

	return React.createElement(
		sticky ? StickyContainer : Fragment,
		sticky ? { collapse: hidden } : undefined,
		<div
			className={classnames(
				'new-search-toolbar',
				className,
				{ hidden },
				{ 'no-click-to-call': !withinCallCenterHours },
			)}
		>
			{isNearMePage ? (
				<div className="new-search-toolbar__header-container">
					<H1
						size="medium"
						color="white"
						weight="extraBold"
						className="new-search-toolbar__heading"
					>
						{headingText}
					</H1>
					<NearMeSubheader
						className="near-me-desktop-subheader"
						geoError={geoError}
						searchLocation={location}
						locateMe={locateMe}
						locating={locating}
						hasCoords={hasCoords}
						isUpdating={isUpdating}
					/>
				</div>
			) : (
				headingText && (
					<H1
						size="medium"
						color="white"
						weight="extraBold"
						className="new-search-toolbar__heading"
					>
						{headingText}
					</H1>
				)
			)}

			{withinCallCenterHours && (
				<ClickToCall
					phone={phone}
					onFetchNumber={actions.getTollfreePhoneNumber}
					phoneIcon
					buttonText="Need Help ?"
				/>
			)}

			<div className="new-search-toolbar__filter-container">
				<Button
					type="button"
					className={classnames('new-search-toolbar__filter', {
						'is-open': filtersShowing,
					})}
					onClick={onOpenFilters}
					aria-label="Open filters"
					aria-expanded={filtersShowing}
				>
					<Filter />
					<Paragraph
						color="white"
						size="small"
						weight="medium"
						last
					>
						Filter
					</Paragraph>
					{filtersCount > 0 && (
						<span
							className="new-search-toolbar__filter-count"
							aria-label={`${filtersCount} filters selected`}
						>
							{filtersCount}
						</span>
					)}
				</Button>
				{/* Desktop filter dropdown - positioned relative to filter button container */}
				<SearchFiltersTray
					filtersShowing={filtersShowing}
					closeFilters={closeFilters}
					searchParams={searchParams}
					onFilter={onFilter}
					searchType={searchType}
					unitFilters={unitFilters}
					facetCounts={facetCounts}
					useNewSearch={useNewSearch}
					className="desktop-filter-tray"
					idPrefix="desktop-"
				/>
			</div>
			{/* Mobile filter tray rendered in NewSearchPage to escape sticky toolbar stacking context */}
			<div className="tool-bar-search-box">
				<SearchBox
					id="new-search-toolbar-box"
					onSearch={onSearch}
					buttonText="Find Storage"
					icon={<MapPin fill="gray" />}
					inputLabel="Enter City or ZIP"
					locationParams={
						locationDetails
							? { location: locationDetails }
							: undefined
					}
					hideAutocompleteOnScroll
					useBrowserLocation
				/>
			</div>
			{isNearMePage && (
				<NearMeSubheader
					className="near-me-mobile-subheader"
					geoError={geoError}
					searchLocation={location}
					locateMe={locateMe}
					locating={locating}
					hasCoords={hasCoords}
					isUpdating={isUpdating}
				/>
			)}
		</div>,
	);
}

NewSearchToolbar.propTypes = {
	/** Dynamic headline sourced from contentTitle (site content API) via headlineSelector */
	headline: PropTypes.string,
	location: PropTypes.object,
	onSearch: PropTypes.func.isRequired,
	onOpenFilters: PropTypes.func.isRequired,
	filtersCount: PropTypes.number,
	className: PropTypes.string,
	phone: phonePropType,
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func.isRequired,
	}),
	sticky: PropTypes.bool,
	hidden: PropTypes.bool,
	withinCallCenterHours: PropTypes.bool.isRequired,
	vehicle: PropTypes.string,
	amenityType: PropTypes.string,
	pageType: PropTypes.string,
	loading: PropTypes.bool,
	// Filter tray props
	filtersShowing: PropTypes.bool,
	closeFilters: PropTypes.func,
	searchParams: PropTypes.object,
	onFilter: PropTypes.func,
	searchType: PropTypes.string,
	unitFilters: PropTypes.object,
	facetCounts: PropTypes.object,
	useNewSearch: PropTypes.bool,
};

export default NewSearchToolbar;
